<script setup>
// Page 3 — what an arrear actually costs, made cinematic. The one full-bleed cobalt
// beat on the page, and deliberately the only one: it sits between the problem and
// the mechanism, and it is about the VvE's own responsibility rather than about
// Pandig. (It used to be a manifesto restating the product promise — which the money
// flow, the feature cards and the FAQ were all restating too. The promise is now made
// once, structurally, on the geldstroom page.)
//
// The section is a couple of screens tall with a sticky inner wrapper, so the words
// hold centre-stage in the viewport while you scroll. A scrubbed timeline lights the
// sentence word by word — each word rising from a dim outline to solid white exactly
// as far as you've scrolled — then the supporting line resolves as the last word
// lands. Pure scroll-driven; nothing autoplays. Because every word starts at DIM
// rather than at zero, the whole statement is legible at the section's top edge, so a
// fling that overshoots into it never lands on an empty cobalt screen — while a jump
// asked for on purpose goes further, to where the sentence is lit (data-jump below).
// Reduced-motion visitors get the settled statement on a single screen.
//
// (This file was Manifesto.vue; same shape, different argument.)
//
// Deliberately not `.snap-page`: it's several viewports tall, and giving an
// oversized box its own scroll-snap-align + stop:always point is what was trapping
// the mouse wheel here (see application.css). It's framed by real `.snap-page`
// panels on both sides, so it just scrolls through freely in between them.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { gsap, ScrollTrigger, SplitText, useMotion, reducedMotion, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const root = ref(null);
const line = ref(null);
const sub = ref(null);

let cleanup = () => {};

onMounted(() => {
  if (reducedMotion) return;

  cleanup = useMotion(root.value, () => {
    const split = SplitText.create(line.value, { type: "words,lines" });
    gsap.set(split.words, { opacity: DIM, yPercent: 40 });
    gsap.set(sub.value, { opacity: DIM, y: 24 });

    gsap
      .timeline({
        scrollTrigger: { trigger: root.value, start: "top top", end: "bottom bottom", scrub: SCRUB },
      })
      .to(split.words, { opacity: 1, yPercent: 0, ease: "none", stagger: 0.4, duration: 1 })
      .to(sub.value, { opacity: 1, y: 0, ease: "power2.out", duration: 0.6 }, ">-0.35");
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Every word starts at DIM, so nothing is lost by arriving mid-scrub — but a
       half-lit sentence on cobalt is the hardest thing on this page to actually read,
       and a jump that hands one over reads as a jump that stopped short. So a jump
       goes to the far end of the range, sentence lit (see scrollElementIntoView). -->
  <section
    id="waarom"
    ref="root"
    class="relative border-y-2 border-foreground bg-primary text-primary-foreground"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[135vh] sm:h-[160vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.stakes')"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-6 sm:py-10">
        <!-- A full sentence rather than a three-word slogan, so the type scale steps
             down from the old manifesto's 8xl: at that size this line wouldn't fit a
             phone page, and the word-by-word lighting is what carries the drama
             anyway, not the point size. -->
        <h2 ref="line" class="max-w-4xl font-sans text-[1.75rem] font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          {{ t("landing.stakes.line") }}
        </h2>
        <p ref="sub" class="mt-4 max-w-2xl text-sm leading-snug text-primary-foreground/85 sm:mt-8 sm:text-lg sm:leading-relaxed">
          {{ t("landing.stakes.sub") }}
        </p>
      </div>
    </div>
  </section>
</template>
